import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { User, Plus, Pencil, Info, AlertTriangle } from 'lucide-react'
import { client } from '@/api/client'
import { CoParentForm } from '@/components/CoParentForm'
import { AuthLayout } from '@/components/AuthLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { UppercaseInput } from '@/components/ui/uppercase-input'
import { Label } from '@/components/ui/label'
import { useAuthStore } from '@/stores/auth'
import { Parent } from '@/types'

interface ProfileCompletionFormData {
  street_name: string
  street_number: string
  city: string
  zip_code: string
  vat_number: string
  social_security_number: string
}

export function ProfileCompletionPage() {
  const { t } = useTranslation(['profile'])
  const navigate = useNavigate()
  const { parent, updateParent } = useAuthStore()
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [showCoParentForm, setShowCoParentForm] = useState(false)
  const [coParent, setCoParent] = useState<Parent | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<ProfileCompletionFormData>({
    defaultValues: {
      street_name: parent?.street_name || '',
      street_number: parent?.street_number || '',
      city: parent?.city || '',
      zip_code: parent?.zip_code || '',
      vat_number: parent?.vat_number || '',
      social_security_number: parent?.social_security_number || '',
    }
  })

  useEffect(() => {
    if (parent?.is_profile_complete) {
      navigate('/')
      return
    }
    if (parent?.co_parent) {
      setCoParent(parent.co_parent)
      verifyCoParentExists(parent.co_parent.id)
    }
  }, [parent, navigate])

  const verifyCoParentExists = async (coParentId: number) => {
    try {
      await client.get(`/co-parents/${coParentId}/`)
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        setCoParent(null)
        if (parent) {
          updateParent({ ...parent, co_parent: null })
        }
      }
    }
  }

  const onSubmit = async (data: ProfileCompletionFormData) => {
    setIsLoading(true)
    setErrorMessage(null)

    try {
      const response = await client.patch('/profile/complete/', data)
      updateParent(response.data)
      navigate('/')
    } catch (err: unknown) {
      const error = err as any
      if (error.response && error.response.data) {
        const errorData = error.response.data
        if (errorData.vat_number) {
          if (errorData.vat_number[0].includes('9 digits')) {
            setError('vat_number', { type: 'manual', message: t('validation.vatFormat', { ns: 'profile' }) })
          } else if (errorData.vat_number[0].includes('check digit')) {
            setError('vat_number', { type: 'manual', message: t('validation.vatInvalid', { ns: 'profile' }) })
          } else if (errorData.vat_number[0].includes('already in use')) {
            setError('vat_number', { type: 'manual', message: t('validation.vatExists', { ns: 'profile' }) })
          }
        }
        if (errorData.social_security_number) {
          if (errorData.social_security_number[0].includes('11 digits')) {
            setError('social_security_number', { type: 'manual', message: t('validation.ssnFormat', { ns: 'profile' }) })
          } else if (errorData.social_security_number[0].includes('already in use')) {
            setError('social_security_number', { type: 'manual', message: t('validation.ssnExists', { ns: 'profile' }) })
          }
        }
        setErrorMessage(t('errors.submit', { ns: 'profile' }))
      } else {
        setErrorMessage(t('errors.submit', { ns: 'profile' }))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleAddCoParent = () => setShowCoParentForm(true)

  const handleCoParentSuccess = (newCoParent: Parent) => {
    setCoParent(newCoParent)
    setShowCoParentForm(false)
    if (parent) {
      updateParent({ ...parent, co_parent: newCoParent })
    }
  }

  return (
    <AuthLayout showLogout maxWidth="max-w-3xl">
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {t('completion.title', { ns: 'profile' })}
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">{t('completion.description', { ns: 'profile' })}</p>
        </CardHeader>
        <CardContent>
          {errorMessage && (
            <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3 mb-4">
              <p className="text-sm text-destructive">{errorMessage}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Personal Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="vat_number">
                  {t('fields.vatNumber', { ns: 'profile' })}
                  <span className="text-muted-foreground text-xs italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
                </Label>
                <Input
                  id="vat_number"
                  {...register('vat_number', {
                    required: t('validation.vatRequired', { ns: 'profile' }),
                    pattern: { value: /^\d{9}$/, message: t('validation.vatFormat', { ns: 'profile' }) }
                  })}
                  placeholder={t('fields.vatNumberPlaceholder', { ns: 'profile' })}
                />
                {errors.vat_number && <p className="text-xs text-destructive">{errors.vat_number.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="social_security_number">
                  {t('fields.socialSecurityNumber', { ns: 'profile' })}
                  <span className="text-muted-foreground text-xs italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
                </Label>
                <Input
                  id="social_security_number"
                  {...register('social_security_number', {
                    required: t('validation.ssnRequired', { ns: 'profile' }),
                    pattern: { value: /^\d{11}$/, message: t('validation.ssnFormat', { ns: 'profile' }) }
                  })}
                  placeholder={t('fields.socialSecurityNumberPlaceholder', { ns: 'profile' })}
                />
                {errors.social_security_number && <p className="text-xs text-destructive">{errors.social_security_number.message}</p>}
              </div>
            </div>

            {/* Address Section */}
            <div>
              <h3 className="text-base font-semibold mb-3">{t('profile.edit.addressSection', { ns: 'profile' })}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="street_name">{t('fields.streetName', { ns: 'profile' })}</Label>
                  <UppercaseInput id="street_name" {...register('street_name', { required: t('validation.streetNameRequired', { ns: 'profile' }) })} placeholder={t('fields.streetNamePlaceholder', { ns: 'profile' })} />
                  {errors.street_name && <p className="text-xs text-destructive">{errors.street_name.message}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="street_number">{t('fields.streetNumber', { ns: 'profile' })}</Label>
                  <UppercaseInput id="street_number" {...register('street_number', { required: t('validation.streetNumberRequired', { ns: 'profile' }) })} placeholder={t('fields.streetNumberPlaceholder', { ns: 'profile' })} />
                  {errors.street_number && <p className="text-xs text-destructive">{errors.street_number.message}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">{t('fields.city', { ns: 'profile' })}</Label>
                  <UppercaseInput id="city" {...register('city', { required: t('validation.cityRequired', { ns: 'profile' }) })} placeholder={t('fields.cityPlaceholder', { ns: 'profile' })} />
                  {errors.city && <p className="text-xs text-destructive">{errors.city.message}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zip_code">{t('fields.zipCode', { ns: 'profile' })}</Label>
                  <Input id="zip_code" {...register('zip_code', { required: t('validation.zipCodeRequired', { ns: 'profile' }) })} placeholder={t('fields.zipCodePlaceholder', { ns: 'profile' })} />
                  {errors.zip_code && <p className="text-xs text-destructive">{errors.zip_code.message}</p>}
                </div>
              </div>
            </div>

            {/* Co-Parent Section */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-base font-semibold">{t('coParent.section.title', { ns: 'profile' })}</h3>
                {!coParent && !showCoParentForm && (
                  <Button type="button" variant="outline" size="sm" onClick={handleAddCoParent}>
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    {t('coParent.actions.add', { ns: 'profile' })}
                  </Button>
                )}
              </div>

              {!showCoParentForm && !coParent && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm space-y-2">
                  <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p>{t('coParent.info.description', { ns: 'profile' })}</p>
                  </div>
                  <p className="pl-6 font-medium">{t('coParent.info.reminder', { ns: 'profile' })}</p>
                  <div className="flex items-start gap-2 pl-6">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="font-medium">{t('coParent.info.important', { ns: 'profile' })}</p>
                  </div>
                </div>
              )}

              {showCoParentForm && (
                <CoParentForm
                  existingCoParent={coParent}
                  onSuccess={handleCoParentSuccess}
                  onRemove={coParent ? () => {
                    setCoParent(null)
                    setShowCoParentForm(false)
                    if (parent) {
                      updateParent({ ...parent, co_parent: null })
                    }
                  } : undefined}
                  isNested={true}
                  parent={parent}
                />
              )}

              {coParent && !showCoParentForm && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold text-sm">{t('coParent.added.title', { ns: 'profile' })}</h4>
                    <Button type="button" variant="outline" size="sm" onClick={handleAddCoParent}>
                      <Pencil className="h-3.5 w-3.5 mr-1" />
                      {t('actions.edit', { ns: 'profile' })}
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="font-medium">{t('fields.name', { ns: 'profile' })}:</span> {coParent.first_name} {coParent.last_name}
                    </div>
                    <div>
                      <span className="font-medium">{t('fields.email', { ns: 'profile' })}:</span> {coParent.email}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? t('completion.submitting', { ns: 'profile' }) : t('completion.submit', { ns: 'profile' })}
            </Button>
          </form>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
